import React from 'react';
import { Calendar, ClipboardList, Loader2 } from 'lucide-react';
import { formatDateIST } from '../utils/date';

const DATE_LABELS = {
  today: 'Today', yesterday: 'Yesterday', week: 'This Week', lastweek: 'Last Week',
  month: 'This Month', lastmonth: 'Last Month', '3months': 'Last 3 Months',
  '6months': 'Last 6 Months', all: 'All', custom: 'Custom',
};

const STATUS_COLOR = {
  waiting: 'bg-amber-50 text-amber-700 border-amber-200',
  in_consult: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-50 text-red-700 border-red-200',
};

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export default function VisitReportsTable({ filter, data, rows, totals, loading }) {
  return (
    <div className="bg-white border rounded-2xl overflow-hidden shadow-sm">
      <div className="px-5 py-3 border-b bg-zinc-50/60 flex items-center gap-3">
        <Calendar className="h-4 w-4 text-zinc-500" />
        <div className="flex-1">
          <div className="text-sm font-bold text-zinc-900">{DATE_LABELS[filter] || 'Selected period'}</div>
          {data?.from && data?.to && (
            <div className="text-[11px] text-zinc-500">
              {formatDateIST(data.from)} — {formatDateIST(data.to)}
            </div>
          )}
        </div>
        <span className="text-xs text-zinc-500">{rows.length} visit{rows.length === 1 ? '' : 's'}</span>
      </div>
      {loading ? (
        <div className="py-12 text-center text-zinc-400">
          <Loader2 className="h-6 w-6 mx-auto animate-spin" />
          <p className="text-sm mt-2">Loading report…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-zinc-400">
          <ClipboardList className="h-8 w-8 mx-auto opacity-40" />
          <p className="text-sm mt-2">No visits found for the selected period.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="report-table">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                {['Date/Time', 'MRN', 'Patient', 'Doctor', 'Visit', 'Status'].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 font-bold">{h}</th>
                ))}
                {['Consult ₹', 'Pharmacy ₹', 'GST ₹', 'Total ₹'].map((h) => (
                  <th key={h} className="text-right px-4 py-2.5 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {rows.map((r, i) => (
                <tr key={r.queue_entry_id} className="hover:bg-blue-50/30 transition" data-testid={`report-row-${i}`}>
                  <td className="px-4 py-2.5 text-[12px]">
                    <div className="font-semibold text-zinc-900">{r.date ? formatDateIST(r.date) : '—'}</div>
                    <div className="text-[10px] text-zinc-500">
                      {r.date ? new Date(r.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : ''}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-zinc-700">{r.mrn}</td>
                  <td className="px-4 py-2.5">
                    <a href={`/patients/${r.patient_id}`} className="text-zinc-900 font-semibold hover:text-blue-700 hover:underline"
                      data-testid={`report-row-patient-${i}`}>{r.patient_name}</a>
                    <div className="text-[10px] text-zinc-500">
                      {r.patient_age ? `${r.patient_age}y` : ''} {r.patient_gender} {r.patient_phone ? `· ${r.patient_phone}` : ''}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-[12px] font-semibold text-zinc-900">{r.doctor_name}</div>
                    {r.session_name && <div className="text-[10px] text-zinc-500">{r.session_name}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">{r.visit_type || 'OP'}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[10px] font-semibold capitalize border px-2 py-0.5 rounded-full ${STATUS_COLOR[r.status] || 'bg-zinc-100 text-zinc-700 border-zinc-200'}`}>
                      {(r.status || '').replace(/_/g, ' ') || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-[12px] font-semibold">{r.consultation_fee ? fmt(r.consultation_fee) : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-[12px] font-semibold">{r.pharmacy_total ? fmt(r.pharmacy_total) : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-[12px] text-zinc-500">{r.pharmacy_gst ? fmt(r.pharmacy_gst) : '—'}</td>
                  <td className="px-4 py-2.5 text-right text-[13px] font-extrabold text-zinc-900">{fmt(r.grand_total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-zinc-50">
              <tr className="text-[11px] font-bold text-zinc-800">
                <td colSpan={6} className="px-4 py-2.5 text-right uppercase tracking-wider text-zinc-500">Totals</td>
                <td className="px-4 py-2.5 text-right">{fmt(totals.consultation_total)}</td>
                <td className="px-4 py-2.5 text-right">{fmt(totals.pharmacy_total)}</td>
                <td className="px-4 py-2.5 text-right">{fmt(totals.pharmacy_gst)}</td>
                <td className="px-4 py-2.5 text-right text-zinc-900 text-[13px]">{fmt(totals.grand_total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
